import { z } from 'zod'
import type { SiteChangeDto, TreeWorkDto } from '../dto/site-history.dto.js'
import { zoneHeadCountSnapshotSchema } from '../dto/site-record-snapshot.js'
import type { SiteSnapshotPatch } from '../dto/site-record-snapshot.js'
import { JOB_TYPE_LABELS } from '../enums/job-type.enum.js'
import type { SiteChangeEntity } from '../enums/site-change.enum.js'
import { siteBoundarySchema } from '../dto/geo.dto.js'
import { polygonAreaAcres } from './geo.js'
import type { HeadType } from '../enums/irrigation.enum.js'
import {
  HEAD_TYPE_LABELS,
  PIPE_SIZE_LABELS,
  pipeSizeSchema,
} from '../enums/irrigation.enum.js'
import {
  SITE_RECORD_FIELDS,
  isAnyTrackedField,
  isTrackedField,
  snapshotValue,
  type AnyTrackedField,
  type SnapshotJson,
} from './site-record.js'

/*
 * One change to a site's record as a line of its timeline: "#41 gone",
 * "Boundary grew 0.3 acres", "Zone 4 gained 2 spray heads". Read off the
 * fields the change logged, as they were and as they became.
 */

const headCountsSchema = z.array(zoneHeadCountSnapshotSchema)

function text(value: SnapshotJson | null): string | null {
  return typeof value === 'string' ? value : null
}

function number(value: SnapshotJson | null): number | null {
  return typeof value === 'number' ? value : null
}

function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? '' : 's'}`
}

function acres(value: SnapshotJson | null): number | null {
  const ring = siteBoundarySchema.safeParse(value)
  return ring.success ? polygonAreaAcres(ring.data.vertices) : null
}

/** "grew 0.3 acres", "shrank 0.1 acres", or "redrawn" when about the same. */
function ringChange(before: SnapshotJson | null, after: SnapshotJson | null): string {
  const was = acres(before)
  const is = acres(after)
  if (was === null || is === null) return 'redrawn'
  const delta = Math.round((is - was) * 10) / 10
  if (delta === 0) return 'redrawn'
  return delta > 0
    ? `grew ${plural(delta, 'acre')}`
    : `shrank ${plural(-delta, 'acre')}`
}

/** "gained 2 spray heads, lost 1 rotor". */
function headChange(
  before: SnapshotJson | null,
  after: SnapshotJson | null,
): string {
  const was = headCountsSchema.safeParse(before)
  const is = headCountsSchema.safeParse(after)
  if (!was.success || !is.success) return 'heads changed'
  const counts = new Map<HeadType, number>()
  for (const entry of was.data) {
    counts.set(entry.type, (counts.get(entry.type) ?? 0) - entry.count)
  }
  for (const entry of is.data) {
    counts.set(entry.type, (counts.get(entry.type) ?? 0) + entry.count)
  }
  const gained: string[] = []
  const lost: string[] = []
  for (const [type, delta] of counts) {
    const noun = `${HEAD_TYPE_LABELS[type].toLowerCase()} head`
    if (delta > 0) gained.push(plural(delta, noun))
    if (delta < 0) lost.push(plural(-delta, noun))
  }
  const parts = [
    gained.length === 0 ? null : `gained ${gained.join(' and ')}`,
    lost.length === 0 ? null : `lost ${lost.join(' and ')}`,
  ].filter((part): part is string => part !== null)
  return parts.length === 0 ? 'heads changed' : parts.join(', ')
}

function size(value: SnapshotJson | null): string | null {
  const parsed = pipeSizeSchema.safeParse(value)
  return parsed.success ? PIPE_SIZE_LABELS[parsed.data] : null
}

/** What one field changing reads as, after the record's name. */
function fieldChange(
  entity: SiteChangeDto['entity'],
  field: AnyTrackedField,
  before: SnapshotJson | null,
  after: SnapshotJson | null,
  names: Readonly<Record<string, string>>,
): string {
  const named = (value: SnapshotJson | null): string | null => {
    const id = text(value)
    return id === null ? null : (names[id] ?? null)
  }
  switch (field) {
    case 'latitude':
    case 'longitude':
      return 'moved'
    case 'boundary':
      return entity === 'BOUNDARY' || entity === 'AREA'
        ? ringChange(before, after)
        : 'outline redrawn'
    case 'headCounts':
      return headChange(before, after)
    case 'dbhInches': {
      const is = number(after)
      const was = number(before)
      if (is === null) return 'trunk measurement cleared'
      return was === null
        ? `measured at ${String(is)} in`
        : `measured at ${String(is)} in (was ${String(was)} in)`
    }
    case 'speciesId': {
      const species = named(after)
      return species === null ? 'species cleared' : `identified as ${species}`
    }
    case 'label': {
      const was = text(before)
      return was === null
        ? 'relabeled'
        : `relabeled from #${was.replace(/^#/, '')}`
    }
    case 'name': {
      const was = text(before)
      return was === null ? 'renamed' : `renamed from ${was}`
    }
    case 'number': {
      const was = number(before)
      return was === null ? 'renumbered' : `renumbered from ${String(was)}`
    }
    case 'partUuid': {
      const part = named(after)
      return part === null ? 'part cleared' : `now ${part}`
    }
    case 'decoderUuid': {
      const decoder = named(after)
      return decoder === null ? 'decoder cleared' : `decoder now ${decoder}`
    }
    case 'timerId':
      return `now on ${named(after) ?? 'another timer'}`
    case 'zoneId':
      return `now on ${named(after) ?? 'another zone'}`
    case 'waterSourceId':
      return `now fed from ${named(after) ?? 'another water source'}`
    case 'size':
    case 'meterSize': {
      const label = size(after)
      return label === null ? 'size cleared' : `now ${label}`
    }
    case 'horsepower': {
      const is = number(after)
      return is === null ? 'horsepower cleared' : `now ${String(is)} hp`
    }
    case 'boxId':
      return text(after) === null
        ? 'taken out of its valve box'
        : 'put in a valve box'
    case 'spliceCount': {
      const is = number(after)
      if (is === null) return 'splices cleared'
      return `now ${String(is)} ${is === 1 ? 'splice' : 'splices'}`
    }
    case 'serialNumber':
      return 'serial number changed'
    case 'kind':
      return 'kind changed'
    case 'backflowType':
    case 'sensorType':
    case 'wellComponentType':
      return 'type changed'
    default: {
      const unhandled: never = field
      return unhandled
    }
  }
}

/** One change, as its line on the site's timeline. */
export function describeChange(
  change: SiteChangeDto,
  names: Readonly<Record<string, string>>,
): string {
  const { subject } = change
  switch (change.action) {
    case 'ADDED':
      return `${subject} added`
    case 'REMOVED':
      if (change.job !== null) return `${subject} removed by us`
      return change.isCorrection
        ? `${subject} deleted, recorded by mistake`
        : `${subject} gone`
    case 'RESTORED':
      return `${subject} put back`
    case 'CHANGED':
      return changedLine(change, names)
  }
}

/**
 * The value a patch holds for one of its fields, or null when the change
 * did not record it. The field name does not select the value's type, so
 * the reader below checks the value itself.
 */
function readField<E extends SiteChangeEntity>(
  patch: SiteSnapshotPatch<E> | null,
  field: keyof (typeof SITE_RECORD_FIELDS)[E] & string,
): SnapshotJson | null {
  if (patch === null) return null
  const value = snapshotValue(patch, field)
  return value === undefined ? null : value
}

/** A change's fields, in the order they were stored, skipping any it does not track. */
function phrases<E extends SiteChangeEntity>(
  entity: E,
  subject: string,
  before: SiteSnapshotPatch<E> | null,
  after: SiteSnapshotPatch<E> | null,
  names: Readonly<Record<string, string>>,
): string {
  const lines: string[] = []
  for (const field of Object.keys(after ?? {})) {
    if (!isTrackedField(entity, field) || !isAnyTrackedField(field)) continue
    const phrase = fieldChange(
      entity,
      field,
      readField(before, field),
      readField(after, field),
      names,
    )
    if (!lines.includes(phrase)) lines.push(phrase)
  }
  return `${subject} ${lines.join(', ')}`
}

function changedLine(
  change: SiteChangeDto,
  names: Readonly<Record<string, string>>,
): string {
  switch (change.entity) {
    case 'BOUNDARY':
      return phrases(
        'BOUNDARY',
        change.subject,
        change.before,
        change.after,
        names,
      )
    case 'AREA':
      return phrases('AREA', change.subject, change.before, change.after, names)
    case 'TREE':
      return phrases('TREE', change.subject, change.before, change.after, names)
    case 'WATER_SOURCE':
      return phrases(
        'WATER_SOURCE',
        change.subject,
        change.before,
        change.after,
        names,
      )
    case 'TIMER':
      return phrases(
        'TIMER',
        change.subject,
        change.before,
        change.after,
        names,
      )
    case 'ZONE':
      return phrases('ZONE', change.subject, change.before, change.after, names)
    case 'EQUIPMENT':
      return phrases(
        'EQUIPMENT',
        change.subject,
        change.before,
        change.after,
        names,
      )
  }
}

/** "a", "a and b", "a, b and c". */
function listed(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1) ?? ''}`
}

/**
 * Work a Housecall Pro job did on one tree, as a line of the timeline:
 * "#41 crown cleaning and crown raise, treated for mistletoe", or "#41
 * removed".
 */
export function describeWork(work: TreeWorkDto): string {
  if (work.removal) return `${work.subject} removed`
  const pruning = work.jobTypes.map((type) =>
    JOB_TYPE_LABELS[type].toLowerCase(),
  )
  const treatments = work.treatments.map((name) => name.toLowerCase())
  const parts = [
    pruning.length === 0 ? null : listed(pruning),
    treatments.length === 0 ? null : `treated for ${listed(treatments)}`,
  ].filter((part): part is string => part !== null)
  return parts.length === 0
    ? `${work.subject} worked on`
    : `${work.subject} ${parts.join(', ')}`
}

function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** One change that is not about a tree, as a sentence for a client. */
function changeSentence(
  change: SiteChangeDto,
  names: Readonly<Record<string, string>>,
): string {
  if (change.entity === 'BOUNDARY') {
    const phrase = ringChange(
      change.before?.boundary ?? null,
      change.after?.boundary ?? null,
    )
    return phrase === 'redrawn'
      ? 'The property’s outline changed.'
      : `The property ${phrase}.`
  }
  switch (change.action) {
    case 'ADDED':
      return `${change.subject} is new.`
    case 'REMOVED':
      return `${change.subject} is no longer there.`
    case 'CHANGED':
    case 'RESTORED':
      return `${capital(describeChange(change, names))}.`
  }
}

/**
 * What changed on a property since an earlier visit, as sentences for a
 * client, oldest first: "We removed #17.", "#3 and #9 are no longer there.",
 * "#43 is new.", "The property grew 0.3 acres.", "Zone 4 gained 2 spray
 * heads." Give it only the real changes (`realChanges`): a correction is not
 * news. A tree measured again is left out, as no board needs it.
 */
export function describeSinceLastVisit(
  changes: readonly SiteChangeDto[],
  names: Readonly<Record<string, string>>,
): string[] {
  const ours: string[] = []
  const gone: string[] = []
  const added: string[] = []
  const others: string[] = []
  for (const change of changes) {
    if (change.entity !== 'TREE') {
      others.push(changeSentence(change, names))
      continue
    }
    if (change.action === 'REMOVED') {
      ;(change.job === null ? gone : ours).push(change.subject)
    } else if (change.action === 'ADDED') {
      added.push(change.subject)
    }
  }
  const verb = (items: readonly string[]): string =>
    items.length === 1 ? 'is' : 'are'
  return [
    ours.length === 0 ? null : `We removed ${listed(ours)}.`,
    gone.length === 0
      ? null
      : `${capital(listed(gone))} ${verb(gone)} no longer there.`,
    added.length === 0 ? null : `${capital(listed(added))} ${verb(added)} new.`,
    ...others,
  ].filter((line): line is string => line !== null)
}
